import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  isSameDay,
  setHours,
  setMinutes,
  setSeconds,
  startOfDay,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import type { CalendarView } from '../types';

export const AGENDA_DAY_SPAN = 7;

export function startOfDayInTimeZone(date: Date, timeZone: string): Date {
  return fromZonedTime(startOfDay(toZonedTime(date, timeZone)), timeZone);
}

export function endOfDayInTimeZone(date: Date, timeZone: string): Date {
  return fromZonedTime(endOfDay(toZonedTime(date, timeZone)), timeZone);
}

export function addDaysInTimeZone(date: Date, days: number, timeZone: string): Date {
  return fromZonedTime(addDays(toZonedTime(date, timeZone), days), timeZone);
}

/** Adds wall-clock minutes in `timeZone` so the result lands on the same clock
 * time even if a DST transition falls between `date` and the result. Plain
 * `addMinutes` adds real elapsed time, which is off by the DST delta whenever
 * a transition falls in between — this instead sets the hour/minute fields
 * directly (via local-getter-based date-fns calls, same trick as
 * `addDaysInTimeZone`), which correctly re-resolves the UTC offset for the
 * resulting wall-clock time. */
export function addMinutesInTimeZone(date: Date, minutes: number, timeZone: string): Date {
  const zoned = toZonedTime(date, timeZone);
  const totalMinutes = zoned.getHours() * 60 + zoned.getMinutes() + minutes;
  const dayOffset = Math.floor(totalMinutes / (24 * 60));
  const minutesOfDay = totalMinutes - dayOffset * 24 * 60;

  const withTime = setSeconds(
    setMinutes(setHours(startOfDay(zoned), Math.floor(minutesOfDay / 60)), minutesOfDay % 60),
    zoned.getSeconds()
  );

  return fromZonedTime(addDays(withTime, dayOffset), timeZone);
}

export function startOfWeekInTimeZone(date: Date, timeZone: string): Date {
  return fromZonedTime(startOfWeek(toZonedTime(date, timeZone)), timeZone);
}

export function endOfWeekInTimeZone(date: Date, timeZone: string): Date {
  return fromZonedTime(endOfWeek(toZonedTime(date, timeZone)), timeZone);
}

export function startOfMonthInTimeZone(date: Date, timeZone: string): Date {
  return fromZonedTime(startOfMonth(toZonedTime(date, timeZone)), timeZone);
}

export function endOfMonthInTimeZone(date: Date, timeZone: string): Date {
  return fromZonedTime(endOfMonth(toZonedTime(date, timeZone)), timeZone);
}

export function isSameDayInTimeZone(a: Date, b: Date, timeZone: string): boolean {
  return isSameDay(toZonedTime(a, timeZone), toZonedTime(b, timeZone));
}

/** 42-cell (6 week) grid of day-starts covering the full weeks that overlap `date`'s month. */
export function buildMonthGrid(date: Date, timeZone: string): Date[] {
  const gridStart = startOfWeekInTimeZone(startOfMonthInTimeZone(date, timeZone), timeZone);
  return Array.from({ length: 42 }, (_, i) => addDaysInTimeZone(gridStart, i, timeZone));
}

/** Inclusive list of day-starts spanning [start, end] in the given time zone. */
export function eachDayInTimeZone(start: Date, end: Date, timeZone: string): Date[] {
  const zonedDays = eachDayOfInterval({
    start: toZonedTime(start, timeZone),
    end: toZonedTime(end, timeZone)
  });
  return zonedDays.map((d) => fromZonedTime(d, timeZone));
}

/** Minutes elapsed from `dayStart` to `date`, both interpreted in `timeZone`. */
export function minutesFromDayStart(date: Date, dayStart: Date, timeZone: string): number {
  const zonedDate = toZonedTime(date, timeZone).getTime();
  const zonedStart = toZonedTime(dayStart, timeZone).getTime();
  return (zonedDate - zonedStart) / 60_000;
}

/** Clip an event's [start, end] to the visible window [windowStart, windowEnd]. */
export function clampRange(
  start: Date,
  end: Date,
  windowStart: Date,
  windowEnd: Date
): { start: Date; end: Date } {
  return {
    start: start < windowStart ? windowStart : start,
    end: end > windowEnd ? windowEnd : end
  };
}

/** Parses an 'HH:mm' string into minutes since midnight. '24:00' is supported as 1440. */
export function parseTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + (minutes ?? 0);
}

/** The visible [start, end) window for a given anchor date + view. */
export function getVisibleRange(
  date: Date,
  view: CalendarView,
  timeZone: string
): { start: Date; end: Date } {
  switch (view) {
    case 'day':
      return {
        start: startOfDayInTimeZone(date, timeZone),
        end: endOfDayInTimeZone(date, timeZone)
      };
    case 'week':
      return {
        start: startOfWeekInTimeZone(date, timeZone),
        end: endOfWeekInTimeZone(date, timeZone)
      };
    case 'month': {
      const grid = buildMonthGrid(date, timeZone);
      return { start: grid[0], end: endOfDayInTimeZone(grid[grid.length - 1], timeZone) };
    }
    case 'agenda': {
      const start = startOfDayInTimeZone(date, timeZone);
      return {
        start,
        end: endOfDayInTimeZone(addDaysInTimeZone(start, AGENDA_DAY_SPAN - 1, timeZone), timeZone)
      };
    }
  }
}

/** Moves the anchor date forward/backward by one view-sized unit. */
export function navigateDate(
  date: Date,
  view: CalendarView,
  direction: 1 | -1,
  timeZone: string
): Date {
  switch (view) {
    case 'day':
      return addDaysInTimeZone(date, direction, timeZone);
    case 'week':
      return addDaysInTimeZone(date, direction * 7, timeZone);
    case 'month': {
      // Navigate from the start of the month so overflowing days (e.g. Jan 31st)
      // don't cause native Date month-overflow (`setMonth` clamping into the
      // following month) and land the user on the wrong month.
      const zoned = toZonedTime(date, timeZone);
      const nextMonthStart = addMonths(startOfMonth(zoned), direction);
      return fromZonedTime(nextMonthStart, timeZone);
    }
    case 'agenda':
      return addDaysInTimeZone(date, direction * AGENDA_DAY_SPAN, timeZone);
  }
}
